import React, { useState, useEffect, useContext, useRef } from "react";
import "./Chat.css";
import { ThemeContext } from "../../contexts/themeConfig";

// libraries
import { HiCheck, HiOutlineClipboard, HiChevronDown, HiChevronRight } from "react-icons/hi2";
import { marked } from "marked";
import CodeHighlighter from "../common/CodeHighlighter";
import { copyToClipboard } from "../../Utility/helper";
import * as DOMPurify from "dompurify";
import { Link, useLocation } from "react-router-dom";
import { message } from "antd";

// components
import ShareDropdown from "./ShareDropdown";
import { toast } from "react-toastify";
import { checkIsValidUrl, getImageLink, getQuestionAndImageUrl, handleDownloadS3Image } from "../../api/fluxImageGenerator";
import { MermaidDiagram } from "../../Utility/mermaid";
import leadsLiftLogo from "../../../src/assests/images/leadslift-logo.png";
import download from "../../../src/assests/images/download.png";

import { leadsLiftSocialAccount } from "../../api/leadsLiftSocial";
import LeadsLiftPopup from "./LeadsLiftPopup";
import { ERROR_FETCHING_ACCOUNTS, NOT_CONNECTED_ERROR } from "../../constants/LeadsLiftConstants";
import LinkedInPostPopup from '../Linkedin/LinkedinPostPopup.tsx';
import { shareToLinkedIn } from "../../api/linkedinApi";
import { LINKEDIN_GET_POST } from "../../constants/Api_constants";

const BotResponse = ({ chatPrompt, response }) => {
  const { theme } = useContext(ThemeContext);
  const [formattedResponseArray, setFormattedResponseArray] = useState();
  const [showTick, setShowTick] = useState(false);
  const [leadsLiftModalOpen, setLeadsLiftModalOpen] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [leadsLiftConnected, setLeadsLiftConnected] = useState(
    localStorage.getItem("leadsLiftConnected") === "true" ? true : false
  );
  const [messageApi, contextHolder] = message.useMessage();
  const location = useLocation();
  const isAgentPath = location.pathname.startsWith("/agents/");

  const [thinkContent, setThinkContent] = useState(null);
  const [isThinkExpanded, setIsThinkExpanded] = useState(false);
  const [bgColor, setBgColor] = useState("#ffffff"); 
  const [fontSize, setFontSize] = useState("16px"); 
  const [textColor, setTextColor] = useState("#000000"); 
  const [themeColor, setThemeColor] = useState("#F9FAFB");

  const [isEmbedded, setIsEmbedded] = useState(false);

  const [s3Link,setS3Link] = useState('');

  const [isLinkedInPopupOpen, setIsLinkedInPopupOpen] = useState(false);
  
  useEffect(() => {
    const embeddedParam = new URLSearchParams(window.location.search).get("embedded") === "true";
    setIsEmbedded(embeddedParam);
  }, []); 

 useEffect(() => {

  const queryParams = new URLSearchParams(location.search);
  let colorFromUrl = queryParams.get("color");
  let textColorFromUrl = queryParams.get("textColor");
  let themeColorFromUrl = queryParams.get("themeColor");

  if (colorFromUrl) {
    colorFromUrl = colorFromUrl === "red" ? "#ff0000" : colorFromUrl;
    setBgColor(colorFromUrl);
  }

  if (textColorFromUrl) {
    setTextColor(textColorFromUrl);
  }

  if (themeColorFromUrl) {
    setThemeColor(themeColorFromUrl);
  }
}, [location]);

  let tickTimeout = false;

  const processThinkContent = (text) => {
    const thinkRegex = /<think>([\s\S]*?)<\/think>/g;
    const matches = [...text.matchAll(thinkRegex)];
    
    if (matches.length > 0) {
      const extracted = matches.map(m => m[1].trim()).join('\n');
      setThinkContent(extracted);
      return text.replace(thinkRegex, '');
    }
    setThinkContent(null);
    return text;
  };


  const replaceCodeBlocks = (text) => {
    const codeBlockRegex = /```(\w+)\s*([\s\S]+?)```/g;
    let match;
    let lastIndex = 0;
    const result = [];

    while ((match = codeBlockRegex.exec(text)) !== null) {
      const language = match[1].toLowerCase();
      const code = match[2].trim();
      const precedingText = text.substring(lastIndex, match.index);
      if (precedingText) {
        result.push({ type: "text", content: precedingText });
      }

      // Add code block as a code object
      result.push({ type: "code", content: code, language });

      lastIndex = match.index + match[0].length;
    }

    const remainingText = text.substring(lastIndex);

    // Add remaining text as a text object
    if (remainingText) {
      result.push({ type: "text", content: remainingText });
    }

    return result;
  };

  useEffect(() => {
    if (response) {
      setIsThinkExpanded(false);
      const cleanedResponse = processThinkContent(response);
      const responseWithoutCodeBlocks = replaceCodeBlocks(cleanedResponse);
      setFormattedResponseArray(responseWithoutCodeBlocks);
    }

    return () => {
      if (tickTimeout) {
        clearTimeout(tickTimeout);
      }
    };
  }, [response]);

  const getMarkedContent = (content) => {
    const result = content.replace(/【.*?】/g, "").replace(/\[0\]/g, "").replace(/<div class="citations-container">.*?<\/div>/g, "");
    const sanitizedContent = DOMPurify.default.sanitize(result);
    const tableHtml = { __html: marked(sanitizedContent) };

    return (
      <div
      id="table-response-container"
      className={`table-${theme}`}
      style={{
      ...(isEmbedded ? { color: textColor } : {}), 
      }}
      >
        <div dangerouslySetInnerHTML={tableHtml} />
      </div>
    );
  };

  const renderThinkContent = () => {
    if (!thinkContent) return null;

    return (
      <div className={`think-container ${theme}`}>
        <button 
          className="think-toggle" 
          onClick={() => setIsThinkExpanded(!isThinkExpanded)}
          aria-expanded={isThinkExpanded}
        >
          <span>Internal Reasoning</span>
          {isThinkExpanded ? (
            <HiChevronDown className="think-chevron-icon" />
          ) : (
            <HiChevronRight className="think-chevron-icon" />
          )}
        </button>
        {isThinkExpanded && (
          <div className="think-content">
            <pre>{thinkContent}</pre>
          </div>
        )}
      </div>
    );
  };

  const onShowTick = () => {
    setShowTick(true);
    if (tickTimeout) {
      clearTimeout(tickTimeout);
    }

    tickTimeout = setTimeout(() => {
      setShowTick(false);
    }, 2000);
  };

  const handleCopyContent = (textToCopy) => {
    const result = textToCopy.replace(/【.*?】/g, "").replace(/\[0\]/g, "").replace(/<div class="citations-container">.*?<\/div>/g, "");
    const isCopied = copyToClipboard(result);
    if (isCopied) onShowTick();
  };

  const handleShareContent = (chatPrompt, textToShare) => {
    try {
      const truncate = (input) =>
        input.length > 100 ? `${input.substring(0, 100)}...` : input;

      const encodedTitle = encodeURIComponent(truncate(chatPrompt));
      const encodedBody = encodeURIComponent(textToShare);

      const draftUrlTemplate = process.env.REACT_APP_GMAIL_DRAFT_URL_TEMPLATE;
      const draftUrl = `${draftUrlTemplate}su=${encodedTitle}&body=${encodedBody}`;

      displayDraftUrlMessage(draftUrl);
    } catch (error) {
      console.error(error);
    }
  };

  const handleLinkedinShareContent = async (chatPrompt, textToShare) => {
    try {
      const response = await shareToLinkedIn(chatPrompt, textToShare);
      if (response) {        
        const postUrl = LINKEDIN_GET_POST(response.id);
        toast.success(
        <div>
          Successfully shared to LinkedIn!{' '}
          <a 
            href={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#0077B5', textDecoration: 'underline' }}
            onClick={(e) => e.stopPropagation()}
          >
            View post
          </a>
        </div>, {
          position: "bottom-left",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setIsLinkedInPopupOpen(false);
      }
    } catch (error) {
      toast.error('Failed to share to LinkedIn. Please try again.', {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const displayDraftUrlMessage = (draftUrl) => {
    if (draftUrl) {
      // Create a message with a link to the draft email
      const message = (
        <div className="draftMessage">
          New email created.
          <Link
            className="draftUrl"
            to={draftUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Gmail
          </Link>
        </div>
      );

      // Display the message as a toast notification
      toast.info(message, {
        position: "bottom-left",
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        // Open the draft email on click
        onClick: () => {
          window.open(draftUrl, "_blank");
        },
      });
    }
  };

  const handleCancel = () => {
    setLeadsLiftModalOpen(false);
  };

  const showModal = async () => {
    if (leadsLiftConnected) {
      try {
        const fetchedAccounts = await leadsLiftSocialAccount(
          localStorage.getItem("leadsLiftLocationId")
        );
        setAccounts(fetchedAccounts?.data?.results?.accounts);
        setLeadsLiftModalOpen(true);
      } catch (error) {
        console.error("Error fetching accounts:", error);
        messageApi.open({
          type: "error",
          content: ERROR_FETCHING_ACCOUNTS,
        });
      }
    } else {
      messageApi.open({
        type: "error",
        content: NOT_CONNECTED_ERROR,
      });
    }
  };

  const { imageUrl, isImageLinkFound } = getImageLink(response);
      
  // Update s3Link in useEffect to avoid infinite loops
  useEffect(() => {
    if (isImageLinkFound) {
      setS3Link(imageUrl);
    }
  }, [imageUrl, isImageLinkFound]);

  return (
    <div className="bot-response-markdown-container">
      {renderThinkContent()}
      <div className="bot-response-main-content">
        {formattedResponseArray?.map((item, index) => {
          if (item.type === "text") {
            const {imageUrl,isImageLinkFound} = getImageLink(item.content);
            return <div>
              {isImageLinkFound ?
                checkIsValidUrl(imageUrl)? <img src={imageUrl}></img> : 
                <pre key={index}>Please connect Flux from Connect Apps <a href="/profile" style={{ textDecoration: "none" }}>page</a></pre> : <pre key={index}>{getMarkedContent(item.content)}</pre>
              }

            </div>;

            // return <pre key={index}>{getMarkedContent(item.content)}</pre>;
          } else if (item.type === "code" && item.language !=="mermaid" ) {
            return (
              <CodeHighlighter
                key={index}
                code={item.content}
                language={item.language}
              />
            );
          }else if (item.type === "code" && item.language ==="mermaid") {
            return (
              <MermaidDiagram
                key={index}
                code={item.content}
                isMermaid={true}
              />
            );
          }
        })}
      </div>

      <div
      className="botMessageWrapper"
      style={{
      ...(isEmbedded ? { backgroundColor: themeColor } : {}), 
      }}
      >
        <div className="bot-response-control-container">
          <div className="bot-response-share-btn">
            <ShareDropdown
              handleShareContent={handleShareContent}
              handleLinkedinShareContent={handleLinkedinShareContent}
              chatPrompt={chatPrompt}
              response={response}
            />
          </div>

          <div className="bot-response-copy-btn">
            <button
              onClick={() => handleCopyContent(response)}
              className="copy-icon"
            >
              {showTick ? (
                <>
                  <HiCheck
                    size={18}
                    color={theme === "light" ? "#000" : "#fff"}
                  />
                  <span
                    className="copy-text"
                    style={
                      theme === "light" ? { color: "#000" } : { color: "#fff" }
                    }
                  >
                    Copied!
                  </span>
                </>
              ) : (
                <>
                  <HiOutlineClipboard
                    size={18}
                    color={theme === "light" ? "#000" : "#fff"}
                  />
                  <span
                    className="copy-text"
                    style={
                      theme === "light" ? { color: "#000" } : { color: "#fff" }
                    }
                  >
                    Copy
                  </span>
                </>
              )}
            </button>
          </div>

          {isAgentPath && (
            <div className="bot-response-leadslift-btn">
              <img
                src={leadsLiftLogo}
                alt="Leads Lift Logo"
                onClick={showModal}
              />
              <LeadsLiftPopup
                props={{
                  isModalOpen: leadsLiftModalOpen,
                  handleCancel: handleCancel,
                  accounts,
                  response,
                }}
              />
              {contextHolder}
            </div>
          )}
          {s3Link && (
            <div className="bot-response-leadslift-btn">
              <img
                src={download}
                alt="download image logo"
                onClick={async ()=>await handleDownloadS3Image(s3Link)}
              />
              {/* <LeadsLiftPopup
                props={{
                  isModalOpen: leadsLiftModalOpen,
                  handleCancel: handleCancel,
                  accounts,
                  response,
                }}
              />
              {contextHolder} */}
            </div>
          )}
        </div>
      </div>
      <LinkedInPostPopup
        isOpen={isLinkedInPopupOpen}
        onClose={() => setIsLinkedInPopupOpen(false)}
        onShare={handleLinkedinShareContent}
        initialText={response}
        chatPrompt={chatPrompt}
      />
    </div>
  );
};


export default BotResponse;
